// ADO-1779 — the Browse overlay rendered into the SiteHeader's `children` slot,
// in the Figma composition for the viewport (useHeaderLayout breakpoints):
//
//   desktop  the column mega-menu under the sub-nav (Figma 4002:16391)
//   tablet   the same three columns, flush and full-bleed, over a scrim that
//            closes the menu when tapped (Figma 4002:17270)
//   mobile   the one-layer-at-a-time side tray (BrowseTray, Figma 4002:17414)
//
// The two scrimmed compositions are modal: the page behind them cannot be
// reached by pointer, so keyboard focus is kept inside too (the shared
// useFocusTrap, as in FiltersDrawer). The desktop menu has no scrim and stays a
// plain disclosure under its trigger.
//
// Shared by the /products header (Products.tsx) and the catalogue-linking
// screens (useBrowseMegaMenu), so the two owners cannot drift. The owner keeps
// the open state and decides what selecting a node does; this only lays it out.

import { useRef, type MouseEvent } from 'react';
import { useFocusTrap, useHeaderLayout } from '../../design-system';
import { BrowseNav, type BrowseNavProps } from './BrowseNav';
import { BrowseTray } from './BrowseTray';
import styles from './browseMegaMenu.module.css';

export interface BrowseMenuProps extends BrowseNavProps {
  /** DOM id of the overlay container (the Browse trigger's `aria-controls`). */
  readonly id: string;
  /** The tablet scrim or the mobile tray's close / scrim was tapped. */
  readonly onClose: () => void;
}

export function BrowseMenu({ id, onClose, ...navProps }: BrowseMenuProps) {
  const layout = useHeaderLayout();
  const tablet = layout === 'tablet';
  const menuRef = useRef<HTMLDivElement>(null);
  const trapTab = useFocusTrap(tablet, menuRef);

  if (layout === 'mobile') {
    return (
      <BrowseTray
        id={id}
        onSelectNode={navProps.onSelectNode}
        onExpandNode={navProps.onExpandNode}
        onClose={onClose}
      />
    );
  }

  // Tablet: the overlay's own box IS the scrim below the columns, so a tap that
  // lands on it (not on a column) is a tap on the scrim.
  const onScrimClick = tablet
    ? (e: MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
      }
    : undefined;
  const modalProps = tablet
    ? {
        role: 'dialog',
        'aria-modal': true,
        'aria-label': 'Browse',
        tabIndex: -1,
        onKeyDown: trapTab,
      }
    : {};

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- the scrim is a pointer affordance only; Escape closes the menu from the keyboard
    <div
      ref={menuRef}
      className={styles.megaMenu}
      id={id}
      data-layout={layout}
      onClick={onScrimClick}
      {...modalProps}
    >
      <BrowseNav {...navProps} />
    </div>
  );
}
